import React, { useState, useEffect } from 'react';
import './HistorialVersiones.css';
import versionesData from '../data/versiones.json';

const HistorialVersiones = () => {
    const [versiones, setVersiones] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        cargarVersiones();
    }, []);

    const cargarVersiones = () => {
        try {
            setLoading(true);
            // Cargar versiones desde el archivo JSON local
            setVersiones(versionesData);
        } catch (error) {
            console.error('Error al cargar versiones:', error);
            setVersiones([]);
        } finally {
            setLoading(false);
        }
    };

    const getTipoIcon = (tipo) => {
        switch (tipo) {
            case 'nueva':
                return '✨';
            case 'mejora':
                return '🔧';
            case 'correccion':
                return '🐛';
            case 'seguridad':
                return '🔒';
            default:
                return '•';
        }
    };

    const getTipoLabel = (tipo) => {
        switch (tipo) {
            case 'nueva':
                return 'Nueva Funcionalidad';
            case 'mejora':
                return 'Mejora';
            case 'correccion':
                return 'Corrección';
            case 'seguridad':
                return 'Seguridad';
            default:
                return 'Cambio';
        }
    };

    const getTipoBadgeClass = (tipo) => {
        switch (tipo) {
            case 'mayor':
                return 'badge-mayor';
            case 'menor':
                return 'badge-menor';
            case 'parche':
                return 'badge-parche';
            default:
                return 'badge-default';
        }
    };

    const formatearFecha = (fecha) => {
        const date = new Date(fecha + 'T00:00:00');
        return date.toLocaleDateString('es-CL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-3 text-muted">Cargando historial de versiones...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4 historial-versiones">
            {/* Header Simple */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0 header-card">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h2 className="mb-1">
                                        <i className="bi bi-clock-history me-2"></i>
                                        Historial de Versiones
                                    </h2>
                                    <p className="text-muted mb-0 small">
                                        Registro de actualizaciones del sistema
                                    </p>
                                </div>
                                <div className="version-badge">
                                    <span className="badge bg-primary px-3 py-2">
                                        v{versiones[0]?.version || '1.0.0'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline de versiones */}
            <div className="row">
                <div className="col-12">
                    {versiones.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-inbox fs-1 text-muted"></i>
                            <p className="text-muted mt-3">No hay versiones registradas</p>
                        </div>
                    ) : (
                        <div className="timeline">
                            {versiones.map((version, index) => (
                                <div
                                    key={version.id}
                                    className="timeline-item"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <div className="timeline-marker">
                                        <div className="timeline-dot"></div>
                                    </div>
                                    <div className="timeline-content">
                                        <div className="card shadow-sm border-0 version-card">
                                            <div className="card-body p-3">
                                                {/* Header de la versión */}
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <div>
                                                        <h5 className="mb-1">
                                                            <span className={`badge ${getTipoBadgeClass(version.tipo)} me-2`}>
                                                                {version.tipo.toUpperCase()}
                                                            </span>
                                                            Versión {version.version}
                                                        </h5>
                                                        <p className="text-muted mb-0 small">
                                                            <i className="bi bi-calendar3 me-1"></i>
                                                            {formatearFecha(version.fecha_release)}
                                                        </p>
                                                    </div>
                                                    <div className="version-stats">
                                                        <span className="badge bg-light text-dark small">
                                                            {version.cambios.length} {version.cambios.length === 1 ? 'cambio' : 'cambios'}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Lista de cambios agrupados por categoría */}
                                                <div className="cambios-list">
                                                    {/* Cambios de Coordinación */}
                                                    {version.cambios.filter(c => c.categoria === 'coordinacion').length > 0 && (
                                                        <div className="mb-4">
                                                            <h6 className="text-primary mb-3">
                                                                <i className="bi bi-people-fill me-2"></i>
                                                                Coordinación
                                                            </h6>
                                                            {version.cambios
                                                                .filter(c => c.categoria === 'coordinacion')
                                                                .map((cambio, idx) => (
                                                                    <div key={idx} className={`cambio-item cambio-${cambio.tipo}`}>
                                                                        <div className="cambio-icon">
                                                                            {getTipoIcon(cambio.tipo)}
                                                                        </div>
                                                                        <div className="cambio-content">
                                                                            <span className="cambio-tipo">
                                                                                {getTipoLabel(cambio.tipo)}
                                                                            </span>
                                                                            <p className="cambio-descripcion mb-0">
                                                                                {cambio.descripcion}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    )}

                                                    {/* Cambios de Financiero */}
                                                    {version.cambios.filter(c => c.categoria === 'financiero').length > 0 && (
                                                        <div>
                                                            <h6 className="text-success mb-3">
                                                                <i className="bi bi-cash-coin me-2"></i>
                                                                Financiero
                                                            </h6>
                                                            {version.cambios
                                                                .filter(c => c.categoria === 'financiero')
                                                                .map((cambio, idx) => (
                                                                    <div key={idx} className={`cambio-item cambio-${cambio.tipo}`}>
                                                                        <div className="cambio-icon">
                                                                            {getTipoIcon(cambio.tipo)}
                                                                        </div>
                                                                        <div className="cambio-content">
                                                                            <span className="cambio-tipo">
                                                                                {getTipoLabel(cambio.tipo)}
                                                                            </span>
                                                                            <p className="cambio-descripcion mb-0">
                                                                                {cambio.descripcion}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HistorialVersiones;

